/**
 * HelpCenterPage — landing for the entire help & support experience.
 *
 * Composition:
 *   - Search bar over published FAQ articles (debounced, queries backend)
 *   - Category cards (counts come from /support/faqs/categories)
 *   - Quick actions: open a ticket, browse your tickets, talk to support bot
 *   - "Popular articles" list (top of the FAQ list)
 *
 * Routes used:
 *   /dashboard/help                — this page
 *   /dashboard/help/articles/:slug — single article
 *   /dashboard/help/tickets        — caller's ticket list
 *   /dashboard/help/tickets/new    — new ticket form
 *   /dashboard/help/tickets/:id    — ticket detail
 *   /dashboard/chat                — Claude support chat (existing)
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { DashboardHeader } from '@/src/modules/components/Header'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import {
  getFaqCategories,
  listFaqs,
  type ApiFaqArticle,
  type FaqCategoryKey,
} from '@/src/infrastructure/lib/api'
import { FAQ_CATEGORY_LABEL } from './constants'

function CategoryCard({
  category,
  count,
}: {
  category: FaqCategoryKey
  count: number
  key?: React.Key
}) {
  return (
    <Link
      to={`/dashboard/help?category=${category}`}
      className="group rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-5 transition-all hover:border-primary/40 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <h3 className="font-semibold text-on-surface group-hover:text-primary transition-colors">
          {FAQ_CATEGORY_LABEL[category]}
        </h3>
        <Icons.Sparkles size={16} className="text-on-surface-variant group-hover:text-primary" />
      </div>
      <p className="mt-2 text-xs text-on-surface-variant">
        {count} {count === 1 ? 'article' : 'articles'}
      </p>
    </Link>
  )
}

export function HelpCenterPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [articles, setArticles] = useState<ApiFaqArticle[]>([])
  const [categories, setCategories] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      listFaqs({ search: debounced || undefined, limit: 12 }),
      getFaqCategories(),
    ])
      .then(([page, cats]) => {
        if (cancelled) return
        setArticles(page.items)
        setCategories(cats)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debounced])

  const categoryKeys = useMemo(
    () => Object.keys(FAQ_CATEGORY_LABEL) as FaqCategoryKey[],
    [],
  )

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 space-y-10 max-w-5xl mx-auto">
      <DashboardHeader
        title="Help & support"
        description="Find answers, browse articles, or talk to our team."
      />

      {/* Hero search */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-outline-variant/40 bg-gradient-to-br from-primary/10 via-surface to-surface-low p-6 md:p-10"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-on-surface">
          How can we help?
        </h2>
        <p className="mt-2 text-sm md:text-base text-on-surface-variant">
          Search articles, FAQs, and troubleshooting guides.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Icons.Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Try "How do I publish?"'
              className="w-full rounded-full border border-outline-variant/40 bg-surface px-12 py-3 text-sm shadow-sm focus:border-primary focus:outline-none"
              aria-label="Search help articles"
            />
          </div>
          <Link
            to="/dashboard/help/tickets/new"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-deep"
          >
            <Icons.Plus size={16} /> Contact support
          </Link>
        </div>
      </motion.section>

      {/* Quick actions */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Link
          to="/dashboard/help/tickets"
          className="rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-5 transition-colors hover:border-primary/40"
        >
          <Icons.History size={20} className="text-primary" />
          <h3 className="mt-3 font-semibold text-on-surface">My tickets</h3>
          <p className="text-xs text-on-surface-variant">Track the status of your requests.</p>
        </Link>
        <Link
          to="/dashboard/help/tickets/new"
          className="rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-5 transition-colors hover:border-primary/40"
        >
          <Icons.Plus size={20} className="text-primary" />
          <h3 className="mt-3 font-semibold text-on-surface">Open a ticket</h3>
          <p className="text-xs text-on-surface-variant">Talk to a human about anything.</p>
        </Link>
        <Link
          to="/dashboard/chat"
          className="rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-5 transition-colors hover:border-primary/40"
        >
          <Icons.Sparkles size={20} className="text-primary" />
          <h3 className="mt-3 font-semibold text-on-surface">Chat with support bot</h3>
          <p className="text-xs text-on-surface-variant">Get an instant answer.</p>
        </Link>
      </section>

      {/* Categories */}
      <section className="space-y-4">
        <h2 className="text-lg font-bold text-on-surface">Browse by category</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {categoryKeys.map((c) => (
            <CategoryCard key={c} category={c} count={categories[c] ?? 0} />
          ))}
        </div>
      </section>

      {/* Articles */}
      <section className="space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-on-surface">
            {debounced ? 'Search results' : 'Popular articles'}
          </h2>
          {debounced && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-xs text-on-surface-variant hover:text-on-surface"
            >
              Clear search
            </button>
          )}
        </header>

        {loading ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : articles.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-outline-variant/50 p-8 text-center">
            <p className="text-sm text-on-surface-variant">
              No articles found. Try a different search or{' '}
              <button
                type="button"
                onClick={() => navigate('/dashboard/help/tickets/new')}
                className="font-semibold text-primary hover:underline"
              >
                contact support
              </button>
              .
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {articles.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/dashboard/help/articles/${a.slug}`}
                  className="block rounded-2xl border border-outline-variant/40 bg-surface-low/60 p-4 hover:border-primary/40 transition-colors"
                >
                  <p
                    className={cn(
                      'text-[10px] font-semibold uppercase tracking-wider text-primary',
                    )}
                  >
                    {FAQ_CATEGORY_LABEL[a.category]}
                  </p>
                  <h3 className="mt-1 font-semibold text-on-surface">{a.title}</h3>
                  {a.excerpt && (
                    <p className="mt-1 text-xs text-on-surface-variant line-clamp-2">
                      {a.excerpt}
                    </p>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
